/**
 * MapZoomControls Component
 *
 * Shared, presentational zoom control cluster (zoom in / zoom out) for maps.
 * Renders two stacked ToolbarIconButton controls. State and handlers are
 * supplied by the consumer via props (no embedded zoom logic).
 *
 * Positioning is left to the consumer through the `className` prop — the
 * cluster itself only owns its vertical stack layout.
 *
 * @module components/ui/MapZoomControls
 */

import { memo } from 'react';
import type { CSSProperties } from 'react';
import clsx from 'clsx';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ToolbarIconButton } from '@/components/ui/ToolbarIconButton';
import { TOOLBOX_ICON_SIZE } from '@/constants';
import classes from './MapZoomControls.module.css';

/**
 * MapZoomControls Props
 *
 * Presentational props — zoom state/handlers are owned by the consumer.
 */
interface MapZoomControlsProps {
  /** Zoom-in click handler */
  onZoomIn: () => void;
  /** Zoom-out click handler */
  onZoomOut: () => void;
  /** Whether the zoom-in button is disabled (max zoom reached) */
  isZoomInDisabled?: boolean;
  /** Whether the zoom-out button is disabled (min zoom reached) */
  isZoomOutDisabled?: boolean;
  /** Visual variant forwarded to the buttons (defaults to 'transparent') */
  variant?: 'light' | 'transparent';
  /** Stack direction of the cluster (defaults to 'vertical') */
  orientation?: 'vertical' | 'horizontal';
  /** Additional CSS class (used for positioning by the consumer) */
  className?: string;
  /** Inline style (CSS custom properties only) for consumer positioning */
  style?: CSSProperties;
}

/**
 * MapZoomControls Component
 *
 * Vertical cluster of zoom-in / zoom-out buttons
 */
export const MapZoomControls = memo(function MapZoomControls({
  onZoomIn,
  onZoomOut,
  isZoomInDisabled = false,
  isZoomOutDisabled = false,
  variant = 'transparent',
  orientation = 'vertical',
  className = '',
  style,
}: MapZoomControlsProps) {
  const { t } = useTranslation();

  return (
    <div
      className={clsx(
        classes.zoomButtons,
        orientation === 'horizontal' && classes.horizontal,
        className
      )}
      style={style}
    >
      <ToolbarIconButton
        icon={<ZoomIn size={TOOLBOX_ICON_SIZE} />}
        tooltip={t('tooltips.map.zoomIn')}
        onClick={onZoomIn}
        disabled={isZoomInDisabled}
        variant={variant}
      />
      <ToolbarIconButton
        icon={<ZoomOut size={TOOLBOX_ICON_SIZE} />}
        tooltip={t('tooltips.map.zoomOut')}
        onClick={onZoomOut}
        disabled={isZoomOutDisabled}
        variant={variant}
      />
    </div>
  );
});
